import { useUrlSearchParams } from '@vueuse/core'
import { inject, type InjectionKey, ref, watch } from 'vue'

import { DataViewType } from '@/constants/relations'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
const useDataViewType = (defaultViewType: DataViewType = DataViewType.list) => {
  const params = useUrlSearchParams<{
    inboxReadMode?: string
    selectedViewType?: DataViewType
  }>('history')

  const selectedViewType = ref<DataViewType>(defaultViewType)

  if (params.selectedViewType) {
    selectedViewType.value = params.selectedViewType
  }

  watch(selectedViewType, () => {
    params.selectedViewType = selectedViewType.value
  })

  // Inbox read-status view ("unread" | "all"). Kept here (alongside selectedViewType)
  // so its value survives navigation (e.g. opening a mail and going back).
  const inboxReadMode = ref<string>(params.inboxReadMode ?? 'unread')

  watch(inboxReadMode, () => {
    params.inboxReadMode = inboxReadMode.value
  })

  return {
    inboxReadMode,
    selectedViewType,
  }
}

type DataViewContext = ReturnType<typeof useDataViewType>

const dataViewTypeContextInjectionKey: InjectionKey<DataViewContext> = Symbol('dataViewTypeContext')
const useDataViewTypeContext = (): DataViewContext => {
  const context = inject(dataViewTypeContextInjectionKey)
  if (!context) {
    throw new Error('useDataViewTypeContext must be used within a DataViewTypeProvider')
  }
  return context
}

const dataViewTypeRelationshipsContextInjectionKey: InjectionKey<DataViewContext> = Symbol(
  'dataViewTypeRelationshipsContext',
)
const useDataViewTypeRelationshipsContext = (): DataViewContext => {
  const context = inject(dataViewTypeRelationshipsContextInjectionKey)
  if (!context) {
    throw new Error(
      'useDataViewTypeRelationshipsContext must be used within a DataViewTypeProvider',
    )
  }
  return context
}

export {
  dataViewTypeContextInjectionKey,
  dataViewTypeRelationshipsContextInjectionKey,
  useDataViewType,
  useDataViewTypeContext,
  useDataViewTypeRelationshipsContext,
}
export type { DataViewContext }
